import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import { InterfacePageEntityComponentType } from '@unifyapps/defs/types/page';
import type { Filter } from '@unifyapps/network/generated/models/filter';
import { Op } from '@unifyapps/network/generated/models/op';
import { EMPTY_ARRAY } from '../../consts/empty';
import type { InterfaceComponent } from '../types/interfaceComponent';

// fields to exclude from page component responses in non-builder mode to reduce payload size
// diffs are stored per device type under properties.devices.<deviceType>.diffs
export const PAGE_COMPONENT_EXCLUDE_FIELDS = [
  'properties.devices.desktop.diffs',
  'properties.devices.tablet.diffs',
  'properties.devices.mobile.diffs',
];

// fields to exclude from data source responses in non-builder mode to reduce payload size
export const DATA_SOURCE_EXCLUDE_FIELDS = ['properties.outputSchema'];

// entity-type-keyed exclude fields for embedded-entities search API (deployed mode)
export const EMBEDDED_ENTITIES_EXCLUDE_FIELDS = {
  e_component: PAGE_COMPONENT_EXCLUDE_FIELDS,
  e_data_source: DATA_SOURCE_EXCLUDE_FIELDS,
};

export const STANDARD_ENTITY_TYPES = {
  Component: UAEntityTypes.Component,
  DataSource: UAEntityTypes.DataSource,
  Interface: UAEntityTypes.Interface,
} as const;

export const DEPLOYED_ENTITY_TYPES = {
  Component: UAEntityTypes.DeployedComponent,
  DataSource: UAEntityTypes.DeployedDataSource,
  Interface: UAEntityTypes.DeployedInterface,
} as const;

/*
 * Fetch only these entities as dependencies since we're not using others, it reduces the response size
 * If this is not done, all such entities which have a field which is a lookup of interface page or interface will be fetched.
 */
export const STANDARD_ALLOWED_ENTITY_TYPES = Object.values(STANDARD_ENTITY_TYPES);
export const DEPLOYED_ALLOWED_ENTITY_TYPES = Object.values(STANDARD_ENTITY_TYPES);

export enum InterfaceComponentType {
  PAGE = 'PAGE',
  MODULE = 'MODULE',
  WALKTHROUGH = 'WALKTHROUGH',
  FORM = 'FORM',
  FIELD = 'FIELD',
  TEMPLATE_COMPONENTS = 'TEMPLATE_COMPONENTS',
}

// Excludes template components from normal page/module/walkthrough listings.
// OR-with-MISSING because legacy components have no isTemplate field at all.
export const EXCLUDE_TEMPLATES_FILTER: Filter = {
  op: Op.OR,
  values: [
    { field: 'properties.isTemplate', op: Op.NOT_EQUAL, values: [true] },
    { field: 'properties.isTemplate', op: Op.MISSING },
  ],
};

// Include-only filter for the template manager / picker queries.
export const ONLY_TEMPLATES_FILTER: Filter = {
  field: 'properties.isTemplate',
  op: Op.EQUAL,
  values: [true],
};

export const PAGE_COMPONENT_FILTER = [
  {
    field: 'properties.componentType',
    op: Op.EQUAL,
    values: [InterfaceComponentType.PAGE],
  },
];

export const MODULE_COMPONENT_FILTER = [
  {
    field: 'properties.componentType',
    op: Op.EQUAL,
    values: [InterfaceComponentType.MODULE],
  },
];

export const MODULE_AND_TEMPLATE_COMPONENT_FILTER = [
  {
    field: 'properties.componentType',
    op: Op.IN,
    values: [InterfaceComponentType.MODULE, InterfaceComponentType.TEMPLATE_COMPONENTS],
  },
];

const WALKTHROUGH_COMPONENT_FILTER = [
  {
    field: 'properties.componentType',
    op: Op.EQUAL,
    values: [InterfaceComponentType.WALKTHROUGH],
  },
];

export const getWalkthroughComponentFilter = (interfacePageId: string) => {
  return [
    {
      field: 'properties.walkthrough.settings.targetInterfacePageId',
      op: Op.EQUAL,
      values: [interfacePageId],
    },
    ...WALKTHROUGH_COMPONENT_FILTER,
  ];
};

export const getLookupInterfaceQueryFilter = ({
  interfaceId,
  interfaceComponent,
  searchFilters = [],
  excludeFields,
}: {
  interfaceId: string;
  interfaceComponent?: InterfaceComponent;
  searchFilters?: Filter[];
  excludeFields?: string[];
}) => {
  let interfaceComponentFilter = EMPTY_ARRAY as Filter[];

  if (interfaceComponent) {
    switch (interfaceComponent.type) {
      case InterfacePageEntityComponentType.MODULE:
      case InterfacePageEntityComponentType.TEMPLATE_COMPONENT:
      case InterfacePageEntityComponentType.WALKTHROUGH:
      case InterfacePageEntityComponentType.FIELD:
      case InterfacePageEntityComponentType.FORM:
        interfaceComponentFilter = [
          {
            field: 'id',
            op: Op.EQUAL,
            values: [interfaceComponent.id],
          },
        ];
        break;
      case InterfacePageEntityComponentType.PAGE: {
        // when set id now because we wait for the interface to be fetched then we figure out the page id from the path and slug
        // and set the id to the interface component
        if (interfaceComponent.id) {
          interfaceComponentFilter = [
            {
              field: 'id',
              op: Op.EQUAL,
              values: [interfaceComponent.id],
            },
          ];
        } else {
          interfaceComponentFilter = [
            {
              op: Op.OR,
              values: [
                {
                  field: 'properties.slug',
                  op: Op.EQUAL,
                  values: [interfaceComponent.slug],
                },
                {
                  // todo: backward compatibility
                  // this filter is for backward compatibility, when slug is not stored in properties of interface component
                  // When every page component has slug in it, then this OR filter won't be needed
                  field: 'id',
                  op: Op.EQUAL,
                  values: [interfaceComponent.slug],
                },
              ],
            },
          ];
        }
        break;
      }
    }
  }

  return {
    filter: {
      op: Op.AND,
      values: [
        { field: 'properties.interfaceId', op: Op.EQUAL, values: [interfaceId] },
        ...interfaceComponentFilter,
        ...searchFilters,
      ],
    },
    ...(excludeFields?.length ? { excludeFields } : {}),
    sorts: [
      {
        field: 'cTm',
        order: 'ASC' as const,
      },
    ],
    page: {
      limit: 200,
      offset: 0,
    },
  };
};
